import React, { useState, useEffect } from "react";
import {
  MDBTabs,
  MDBTabsItem,
  MDBTabsLink,
  MDBTabsContent,
  MDBTabsPane,
  MDBCard,
  MDBCardBody,
} from "mdb-react-ui-kit";
import Meetings from "./Meetings";
import Bills from "./Bills";
import Agendas from "./Agendas";
import Details from "./Details";
import ReactGA from "react-ga4";
import Journals from "./Journals";

function Tabs({
  additionalTabs,
  type = "standing",
  enabledTabs,
  id,
  name,
  allMeetings,
  showOnlyMeetings,
  legislativeCalendarDays,
  legislatureOrdinal,
  committeeData,
}) {
  const [activeTab, setActiveTab] = useState("");
  const [defaultTabs, setDefaultTabs] = useState([]);

  useEffect(() => {
    if (showOnlyMeetings) {
      setDefaultTabs([
        {
          title: "Details",
          content: (
            <Details id={id} type={type} committeeData={committeeData} />
          ),
        },
        {
          title: "Agendas",
          content: (
            <Agendas
              legislativeCalendarDays={legislativeCalendarDays}
              committeeData={committeeData}
              type={type}
            />
          ),
        },
        {
          title: "Journals",
          content: (
            <Journals
              legislativeCalendarDays={legislativeCalendarDays}
              committeeData={committeeData}
              type={type}
            />
          ),
        },
      ]);
    } else if (type === "standing") {
      setDefaultTabs([
        {
          title: "Details",
          content: <Details id={id} name={name} type={type} />,
        },
        { 
          title: "Meetings", 
          content: (
            <Meetings 
              id={id} 
              type={type} 
              legislatureOrdinal={legislatureOrdinal} 
              committeeData={committeeData}
              isActive={activeTab === "Meetings"}
            />
          )
        },
        { 
          title: "Bills", 
          content: (
            <Bills 
              id={id} 
              name={name} 
              type={type}
              isActive={activeTab === "Bills"}
            />
          )
        },
        { title: "Exhibits", content: "" },
      ]);
    } else if (type === "nonStanding") {
      setDefaultTabs([
        {
          title: "Details",
          content: <Details id={id} name={name} type={type} />,
        },
        { 
          title: "Meetings", 
          content: (
            <Meetings 
              id={id} 
              type={type} 
              legislatureOrdinal={legislatureOrdinal} 
              committeeData={committeeData}
              isActive={activeTab === "Meetings"}
            />
          )
        },
      ]);
    }
  }, [
    type,
    showOnlyMeetings,
    id,
    name,
    legislativeCalendarDays,
    committeeData,
    activeTab,
    legislatureOrdinal
  ]);

  useEffect(() => {
    const filteredDefaultTabs = showOnlyMeetings ? defaultTabs : defaultTabs.filter(
      (tab) => tab.title === "Details" ||
        enabledTabs.includes(tab.title.toUpperCase())
    );
    const allTabs = [...filteredDefaultTabs, ...additionalTabs];
    if (allTabs.length > 0) {
      // Parse the tab parameter from the hash portion of the URL.
      // For example, if the URL is "#/standingCommittees/35?tab=Meetings"
      const hash = window.location.hash;
      let tabParam = "";
      if (hash.includes("?")) {
        const queryString = hash.split("?")[1];
        const params = new URLSearchParams(queryString);
        tabParam = params.get("tab") || "";
      }
      const validTab = allTabs.find(
        (tab) => tab.title.toLowerCase() === tabParam.toLowerCase()
      );
      setActiveTab(validTab ? validTab.title : allTabs[0].title);
    }
  }, [defaultTabs, additionalTabs, enabledTabs]);

  const handleTabClick = (tabTitle) => {
    ReactGA.event({
      category: "View Type",
      action: `${name}-${id}-${tabTitle} clicked`,
      label: `${name}-${id}-${tabTitle} clicked`,
    });

    setActiveTab(tabTitle);
    // Update the tab parameter in the hash portion of the URL.
    const currentUrl = window.location.href;
    const [baseUrl, hashString = ""] = currentUrl.split("#");
    let hashBase = hashString;
    let params = new URLSearchParams();
    if (hashString.includes("?")) {
      [hashBase] = hashString.split("?");
      const queryString = hashString.split("?")[1];
      params = new URLSearchParams(queryString);
    }
    params.set("tab", tabTitle);
    const newHash = `${hashBase}?${params.toString()}`;
    const newUrl = `${baseUrl}#${newHash}`;
    window.history.replaceState(null, "", newUrl);
  };

  return (
    <div className="mb-4">
      {/* Tabs Header */}
      <MDBTabs>
        {[
          ...defaultTabs.filter((tab) => showOnlyMeetings ? defaultTabs :
                  (tab.title === "Details" ||
              enabledTabs.includes(tab.title.toUpperCase()))),
          ...additionalTabs,
        ].map((tab, index) => (
          <MDBTabsItem key={index}>
            <MDBTabsLink
              className="border border-bottom-0"
              onClick={() => handleTabClick(tab.title)}
              active={activeTab === tab.title}
            >
              {tab.title}
            </MDBTabsLink>
          </MDBTabsItem>
        ))}
      </MDBTabs>
      <MDBCard className="rounded-0 tab-card">
        <MDBCardBody className="p-4 tab-card-body">
          {/* Tabs Content */}
          <MDBTabsContent>
            {[
              ...defaultTabs.filter((tab) => showOnlyMeetings ? defaultTabs :
                  (tab.title === "Details" ||
                      enabledTabs.includes(tab.title.toUpperCase()))),
              ...additionalTabs,
            ].map((tab, index) => (
              <MDBTabsPane key={index} open={activeTab === tab.title}>
                {additionalTabs.includes(tab) ? tab.content.map((c) => {
                  return (
                    <div className="tab-section">
                      {c.sectionTitle}
                      <div className="p-3 tab-section-body"
                        dangerouslySetInnerHTML={{__html: c.sectionContent}} />
                    </div>
                )}) : (
                    tab.content || "Content Coming Soon."
                )}
              </MDBTabsPane>
            ))}
          </MDBTabsContent>
        </MDBCardBody>
      </MDBCard>
    </div>
  );
}

export default Tabs;
